import { FC, useMemo } from "react";
import { Nullable, useVehicleTypeControllerDeleteVehicleType } from "service";

import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";

import { useNavigate } from "react-router-dom";
import { generateRoute } from "ui";
import { AdRoutes } from "../../../constants/ad-routes";
import { AdminVehicleTypeResDto } from "service/api/estabrookSchemas";
import { formatDateTime } from "../../../utils/string-helpers";
import InteractableCell from "../components/generic-table/interactable-cell";
import { toast } from "react-toastify";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "../../../query-client";
import { useDialogStore } from "../../../state/dialog-store";

const sanitizedHeader = [
  {
    label: "Type",
    value: "typeName",
    sortableValue: "typeName",
    sortable: true,
    size: "30%",
  },
  {
    label: "Travel Speed",
    value: "travelSpeed",
    sortable: true,
    sortableValue: "travelSpeed",
    size: "30%",
  },
  {
    label: "Last Updated",
    value: "lastUpdated",
    sortable: true,
    sortableValue: "lastUpdated",
    size: "30%",
  },
  {
    label: "",
    value: "actions",
    size: "10%",
  },
];

export interface VehicleTypesTableProps {
  data?: AdminVehicleTypeResDto[];
  sortOptions: Nullable<SortOptions>;
  sortOptionsChange: (value: Nullable<SortOptions>) => void;
  isLoading: boolean;
}

const VehicleTypesTable: FC<VehicleTypesTableProps> = (props) => {
  const { data = [], sortOptions, sortOptionsChange, isLoading } = props;
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();

  const onVehicleDetail = (id: number) => {
    navigate(generateRoute(AdRoutes.editVehicleType, { typeId: id }));
  };

  const { mutate: removeVehicle } = useVehicleTypeControllerDeleteVehicleType({
    onSuccess: () => {
      toast.success("Vehicle Type Deleted Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicle-types",
          operationId: "vehicleTypeControllerFindMany",
          variables: {},
        }),
      );
    },
  });

  const onDeleteVehicle = (id: number, typeName: string) => {
    setDialog({
      title: "Delete Vehicle Type",
      description: `Are you sure you want to delete vehicle type - ${typeName}?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      withCancelAction: true,
      onConfirm: async (onClose) => {
        removeVehicle({ pathParams: { typeId: id || 0 } });
        onClose();
      },
    });
  };

  const renderRowLabel: Record<
    string,
    RenderGenericRowFunction<AdminVehicleTypeResDto>
  > = useMemo(
    () => ({
      typeName: ({ headerValue, data: { typeName } }) => ({
        label: typeName,
        value: headerValue,
      }),
      travelSpeed: ({ headerValue, data: { travelSpeed } }) => ({
        label: `${travelSpeed} mph`,
        value: headerValue,
      }),
      lastUpdated: ({ headerValue, data: { lastUpdated } }) => ({
        label: formatDateTime(lastUpdated, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        value: headerValue,
      }),
      actions: ({ headerValue, data }) => {
        const { id, typeName } = data;
        return {
          label: (
            <InteractableCell
              label={""}
              onDelete={() => onDeleteVehicle(id, typeName)}
              onEdit={() => onVehicleDetail(id)}
            />
          ),
          value: headerValue,
        };
      },
    }),
    [],
  );
  return (
    <GenericTable
      sx={{ mt: "20px" }}
      headers={sanitizedHeader}
      renderRowLabel={renderRowLabel}
      data={data}
      sortOptions={sortOptions}
      onSortChange={sortOptionsChange}
      isLoading={isLoading}
    />
  );
};

export default VehicleTypesTable;
